import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AnimatedText from "@/components/AnimatedText";
import { cn } from "@/lib/utils";
import { siteConfig } from "@/config";
import { useIsMobile } from "@/hooks/use-mobile";
import TutorialCard from "../components/TutorialCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import PageTransition from "@/components/PageTransition";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Separator } from "@/components/ui/separator";
import {
  IconSearch,
  IconFilter,
  IconBook,
  IconCode,
  IconStar,
  IconTrendingUp,
} from "@tabler/icons-react";

const Tutorials = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const tutorialsRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const autoAnimationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
    document.title = `${siteConfig.tutorials.title} | Tutorials`;
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Mobile auto-cycling
  useEffect(() => {
    if (isMobile && filteredTutorials.length > 0 && isVisible) {
      const startAutoAnimation = () => {
        let currentIndex = 0;
        const cycleTutorials = () => {
          setActiveIndex(currentIndex);
          currentIndex = (currentIndex + 1) % filteredTutorials.length;
          if (autoAnimationTimerRef.current) {
            clearTimeout(autoAnimationTimerRef.current);
          }
          autoAnimationTimerRef.current = setTimeout(cycleTutorials, 2500);
        };
        cycleTutorials();
      };
      startAutoAnimation();
      return () => {
        if (autoAnimationTimerRef.current) {
          clearTimeout(autoAnimationTimerRef.current);
        }
      };
    }
  }, [isMobile, isVisible, activeFilter, search]);

  const tutorialCategories = [
    { id: "all", label: "All Tutorials", icon: IconBook },
    ...siteConfig.tutorials.categories.map((cat: any) => ({
      id: cat.id,
      label: cat.name,
      icon: IconCode,
    })),
  ];

  const filteredTutorials = siteConfig.tutorials.items.filter(
    (tutorial: any) => {
      const matchesCategory =
        activeFilter === "all" || tutorial.category === activeFilter;
      const matchesSearch =
        tutorial.title.toLowerCase().includes(search.toLowerCase()) ||
        tutorial.description.toLowerCase().includes(search.toLowerCase()) ||
        (tutorial.tags &&
          tutorial.tags.some((tag: string) =>
            tag.toLowerCase().includes(search.toLowerCase())
          ));
      return matchesCategory && matchesSearch;
    }
  );

  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
    setActiveIndex(null);
  };

  const handleTutorialHover = (index: number | null) => {
    if (!isMobile) {
      setActiveIndex(index);
    }
  };

  const totalTutorials = siteConfig.tutorials.items.length;
  const featuredTutorials = siteConfig.tutorials.items.filter(
    (t: any) => t.featured
  ).length;

  return (
    <PageTransition>
      <div className="bg-black text-white min-h-screen relative overflow-hidden">
        {/* Animated background elements - matching TutorialDetail */}
        <div className="fixed inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/10 via-purple-900/10 to-pink-900/10"></div>
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-green-500/5 rounded-full blur-3xl animate-pulse delay-500"></div>
          <div className="noise-bg opacity-20"></div>
        </div>

        <header className="relative z-50">
          <Navbar />
        </header>

        <main
          id="main-content"
          className="relative z-10 container mx-auto px-4 pt-28 md:pt-36 pb-20 md:pb-32"
        >
          {/* Enhanced Hero Section */}
          <section className="mb-20">
            <div className="relative mb-16">
              <div className="absolute -inset-8 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 rounded-3xl blur-xl"></div>
              <div className="relative">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-white/10">
                    <IconBook size={32} className="text-blue-400" />
                  </div>
                  <div className="flex items-center gap-6">
                    <Badge className="bg-gradient-to-r from-green-500/20 to-green-600/20 border-green-500/30 text-green-200 px-4 py-2 flex items-center gap-2">
                      <IconStar size={16} />
                      {featuredTutorials} Featured
                    </Badge>
                    <Badge className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 border-blue-500/30 text-blue-200 px-4 py-2 flex items-center gap-2">
                      <IconTrendingUp size={16} />
                      {totalTutorials} Total
                    </Badge>
                  </div>
                </div>

                <h1 className="font-display text-4xl md:text-7xl tracking-tight mb-6 text-white leading-tight">
                  <AnimatedText text={siteConfig.tutorials.title} delay={100} />
                </h1>

                <div className="text-gray-300 mb-12 text-lg leading-relaxed max-w-3xl">
                  {siteConfig.tutorials.description}
                </div>
              </div>
            </div>

            <Separator className="mb-12 bg-gradient-to-r from-transparent via-white/20 to-transparent" />

            {/* Enhanced Filters and Search */}
            <div className="space-y-8">
              {/* Category Filters */}
              <div
                className={cn(
                  "transition-all duration-700 delay-200",
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-10"
                )}
              >
                <div className="flex items-center gap-3 mb-6">
                  <IconFilter size={20} className="text-blue-400" />
                  <span className="text-sm uppercase tracking-widest text-gray-400">
                    Filter by Category
                  </span>
                </div>
                <div className="flex flex-wrap gap-4">
                  {tutorialCategories.map((category, index) => {
                    const Icon = category.icon;
                    return (
                      <Button
                        key={category.id}
                        variant="ghost"
                        className={cn(
                          "relative px-6 py-3 rounded-xl border transition-all duration-500 group overflow-hidden",
                          "hover:scale-105 hover:shadow-lg hover:shadow-blue-500/20",
                          activeFilter === category.id
                            ? "bg-gradient-to-r from-blue-500/20 to-purple-500/20 border-blue-500/50 text-blue-200 shadow-lg shadow-blue-500/20"
                            : "border-white/20 text-gray-300 hover:border-blue-500/50 hover:text-blue-200 hover:bg-blue-500/10"
                        )}
                        style={{ transitionDelay: `${index * 100}ms` }}
                        onClick={() => handleFilterChange(category.id)}
                      >
                        <div className="flex items-center gap-3 relative z-10">
                          <Icon size={18} />
                          <span className="font-medium tracking-wide">
                            {category.label}
                          </span>
                        </div>

                        {/* Animated background */}
                        <div
                          className={cn(
                            "absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 transition-all duration-300",
                            activeFilter === category.id
                              ? "opacity-100"
                              : "opacity-0 group-hover:opacity-100"
                          )}
                        ></div>
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Enhanced Search */}
              <div
                className={cn(
                  "transition-all duration-700 delay-400",
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-10"
                )}
              >
                <div className="flex items-center gap-3 mb-4">
                  <IconSearch size={20} className="text-purple-400" />
                  <span className="text-sm uppercase tracking-widest text-gray-400">
                    Search Tutorials
                  </span>
                </div>
                <div className="relative max-w-md">
                  <div
                    className={cn(
                      "absolute -inset-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl blur-lg transition-all duration-300",
                      searchFocused ? "opacity-100" : "opacity-0"
                    )}
                  ></div>
                  <div className="relative">
                    <IconSearch
                      size={20}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10"
                    />
                    <Input
                      type="text"
                      placeholder="Search by title, description, or tags..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onFocus={() => setSearchFocused(true)}
                      onBlur={() => setSearchFocused(false)}
                      className={cn(
                        "pl-12 pr-4 py-3 bg-black/50 backdrop-blur-sm border-white/20 text-white placeholder:text-gray-500 rounded-xl",
                        "focus:border-blue-500/50 focus:bg-black/70 transition-all duration-300",
                        "hover:border-white/30"
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Results Summary */}
          <div
            className={cn(
              "flex items-center justify-between mb-12 transition-all duration-700 delay-600",
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-10"
            )}
          >
            <div className="flex items-center gap-4">
              <h2 className="text-2xl font-semibold text-white">
                {search
                  ? `Search Results`
                  : activeFilter === "all"
                  ? "All Tutorials"
                  : tutorialCategories.find((c) => c.id === activeFilter)
                      ?.label}
              </h2>
              <Badge className="bg-white/10 border-white/20 text-gray-300 px-3 py-1">
                {filteredTutorials.length}{" "}
                {filteredTutorials.length === 1 ? "tutorial" : "tutorials"}
              </Badge>
            </div>

            {search && (
              <Button
                variant="ghost"
                onClick={() => setSearch("")}
                className="text-gray-400 hover:text-white transition-colors"
              >
                Clear search
              </Button>
            )}
          </div>

          {/* Enhanced Tutorials Grid */}
          <section ref={sectionRef}>
            {filteredTutorials.length === 0 ? (
              <div className="text-center py-20">
                <div className="mb-6">
                  <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center mb-4">
                    <IconSearch size={40} className="text-gray-500" />
                  </div>
                </div>
                <h3 className="text-2xl font-semibold mb-4 text-gray-300">
                  No tutorials found
                </h3>
                <p className="text-gray-500 mb-8 max-w-md mx-auto">
                  {search
                    ? `No tutorials match your search "${search}". Try different keywords or browse all tutorials.`
                    : "No tutorials available in this category yet. Check back soon for new content!"}
                </p>
                {search && (
                  <Button
                    onClick={() => setSearch("")}
                    className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white px-6 py-2 rounded-xl"
                  >
                    Show all tutorials
                  </Button>
                )}
              </div>
            ) : (
              <div
                ref={tutorialsRef}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
              >
                {filteredTutorials.map((tutorial: any, index: number) => (
                  <div
                    key={tutorial.id}
                    className={cn(
                      "transition-all duration-700",
                      isVisible
                        ? "opacity-100 translate-y-0"
                        : "opacity-0 translate-y-10"
                    )}
                    style={{ transitionDelay: `${(index % 6) * 100 + 800}ms` }}
                  >
                    <TutorialCard
                      id={tutorial.id}
                      title={tutorial.title}
                      description={tutorial.description}
                      image={tutorial.thumbnail}
                      tags={tutorial.tags}
                      difficulty={tutorial.difficulty}
                      duration={tutorial.duration}
                      techStack={tutorial.techStack}
                      dateAdded={tutorial.dateAdded}
                      isActive={isMobile ? activeIndex === index : undefined}
                      onMouseEnter={() => handleTutorialHover(index)}
                      onMouseLeave={() => handleTutorialHover(null)}
                      onClick={() => navigate(`/tutorials/${tutorial.slug}`)}
                    />
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Call to Action */}
          {filteredTutorials.length > 0 && (
            <section
              className={cn(
                "mt-20 text-center transition-all duration-700 delay-1000",
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              )}
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 rounded-3xl blur-xl"></div>
                <div className="relative bg-black/30 backdrop-blur-sm border border-white/10 rounded-2xl p-8 md:p-12">
                  <h3 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                    Ready to start learning?
                  </h3>
                  <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
                    Dive into our comprehensive tutorials and build amazing
                    projects with the latest technologies.
                  </p>
                  <div className="flex flex-wrap gap-4 justify-center">
                    <Button
                      onClick={() =>
                        window.scrollTo({ top: 0, behavior: "smooth" })
                      }
                      className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white px-8 py-3 rounded-xl font-medium"
                    >
                      Explore More
                    </Button>
                    <Button
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white hover:text-black px-8 py-3 rounded-xl font-medium"
                    >
                      View All Categories
                    </Button>
                  </div>
                </div>
              </div>
            </section>
          )}
        </main>

        <footer className="relative z-10">
          <Footer />
        </footer>
      </div>
    </PageTransition>
  );
};

export default Tutorials;
